import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ItemCountBadge from "../ItemCountBadge";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";
import InventoryTable from "src/views/inventory/requests/orders/InventoryTable";
import ManualLocationKit from "./manual-location-kits/ManualLocationKit";
import GuidedPickingModal from "./GuidedPickingModal";
import InternalWorkOrderGuidedWizardModal from "./internal-work-order-guided-wizard/InternalWorkOrderGuidedWizardModal";
import useMutation from "../../../../hooks/useMutation";
import useQueryParams from "../../../../hooks/useQueryParams";
import { UPDATE_INTERNAL_WORK_ORDER_MUTATION } from "src/constants/graphql/mutations";
import InternalWorkOrderHeader from "../InternalWorkOrderHeader";
import { isVaSubdomain } from "src/utils/inventoryRequests";
import PalletsBuiltField from "./PalletsBuiltField";
import { getInventoryRequestFields } from "src/views/inventory/utils";
import useInventoryItemsFilter from "../../../../hooks/useInventoryItemsFilter";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import { extractBillingTypes } from "src/utils/billingHelpers";
import { INTERNAL_WORK_ORDER_QUERY } from "src/constants/graphql/queries";
import useRoles from "../../../../hooks/user/useRoles";

export default function InternalWorkOrderPickingModal({ id, onClose }) {
  const navigate = useNavigate();
  const hasAssistantPermission = useRoles(["admin", "manager", "inventory"]);
  const params = useQueryParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const showGuidedWizard = params.get("internalWorkOrderGuidedWizard");
  const [openPalletsBuiltCountModal, setOpenPalletsBuiltCountModal] = useState(false);
  // use internal work order query
  const { loading, data: { internalWorkOrder = {} } = {} } = useQuery(INTERNAL_WORK_ORDER_QUERY, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: false,
  });
  const [saveInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const [openKitModal, setOpenKitModal] = useState(false);
  const [kitRackLocations, setKitRackLocations] = useState([]);
  const [selectedKitAssetId, setSelectedKitAssetId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showGuidedPicking, setShowGuidedPicking] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const isVa = isVaSubdomain();
  const { data: { project = {} } = {} } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: internalWorkOrder?.projectId },
    skip: !internalWorkOrder?.projectId,
    fetchPolicy: "cache-first", // Use cache to avoid repeated queries
  });
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const billingType = useMemo(() => {
    const inboundBilling = project?.billingTypes?.outbound;
    return extractBillingTypes(inboundBilling, project?.billingType)?.types[0];
  }, [project]);

  const inventoryItems = internalWorkOrder.internalWorkOrderItems || [];

  const sortedInventoryItems = [...inventoryItems].sort((a, b) => {
    const aIsKit = a.asset?.type === "Kit";
    const bIsKit = b.asset?.type === "Kit";

    if (aIsKit && !bIsKit) return 1;
    if (!aIsKit && bIsKit) return -1;
    if (aIsKit && bIsKit) return 0;

    const aLocation = a.rackLocations[0]?.warehouse_locations?.[0]?.location_name || "";
    const bLocation = b.rackLocations[0]?.warehouse_locations?.[0]?.location_name || "";
    return aLocation.localeCompare(bLocation);
  });

  const {
    searchTerm,
    filteredItems: filteredInventoryItems,
    handleSearchChange,
  } = useInventoryItemsFilter(sortedInventoryItems);

  // Create warehouse lookup map from warehouseId to name
  const warehouseLookup = useMemo(() => {
    const lookup = {};
    if (internalWorkOrder.warehousesFromInternalItems) {
      internalWorkOrder.warehousesFromInternalItems.forEach(warehouse => {
        lookup[warehouse.warehouseId] = warehouse.name;
      });
    }
    return lookup;
  }, [internalWorkOrder.warehousesFromInternalItems]);

  // Group items by warehouse for visual separation
  const itemsGroupedByWarehouse = useMemo(() => {
    const grouped = {};
    filteredInventoryItems.forEach(item => {
      const warehouseIds = item.rackLocations?.map(rl => rl.warehouse?.warehouse_id).filter(Boolean) || [];
      const uniqueWarehouseIds = [...new Set(warehouseIds)];

      if (uniqueWarehouseIds.length === 0) {
        // If no warehouse specified, group under 'Unknown'
        if (!grouped["Unknown"]) grouped["Unknown"] = [];
        grouped["Unknown"].push(item);
      } else {
        // Group item under each warehouse it belongs to (using warehouse name as key)
        uniqueWarehouseIds.forEach(warehouseId => {
          const warehouseName = warehouseLookup[warehouseId] || warehouseId;
          if (!grouped[warehouseName]) grouped[warehouseName] = [];
          grouped[warehouseName].push(item);
        });
      }
    });
    return grouped;
  }, [filteredInventoryItems, warehouseLookup]);

  const hasMultipleWarehouses = Object.keys(itemsGroupedByWarehouse).length > 1;

  const showBarcodeColumn = useMemo(() => {
    return filteredInventoryItems.some(item => item.asset?.barcode);
  }, [filteredInventoryItems]);

  const handlePickKit = (kitAssetId, rackLocations, item) => {
    setSelectedKitAssetId(kitAssetId);
    setKitRackLocations(rackLocations);
    setSelectedItem(item);
    setOpenKitModal(true);
  };

  const changeStatus = useCallback(
    key => async checked => {
      const input = {
        vaStatus: {
          [key]: checked,
        },
      };
      await saveInternalWorkOrder({
        variables: { id: internalWorkOrder.id, input },
      });
    },
    [internalWorkOrder, hasAdditionalCharges, saveInternalWorkOrder]
  );

  const handleStartPicking = () => {
    const newParams = new URLSearchParams(params);
    newParams.set("internalWorkOrderGuidedWizard", id);
    newParams.set("step", "details");
    navigate(`?${newParams.toString()}`);
  };

  const handleGuidedPickingClose = () => {
    setShowGuidedPicking(false);
  };

  const handleGuidedWizardClose = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete("internalWorkOrderGuidedWizard");
    newParams.delete("step");
    if (isMobile) {
      newParams.delete("internalWorkOrderPicking");
    }
    navigate(`?${newParams.toString()}`);
  };

  const handlePalletsBuiltCountSubmit = billingQuantities => {
    saveInternalWorkOrder({
      variables: {
        id: internalWorkOrder.id,
        input: getInventoryRequestFields({
          ...internalWorkOrder,
          billingQuantities,
        }),
      },
    });
  };

  const allItemsPicked = sortedInventoryItems.every(item => item.pickedStatus);

  useEffect(() => {
    if (isMobile && !showGuidedWizard) {
      const newParams = new URLSearchParams(params);
      newParams.set("internalWorkOrderGuidedWizard", id);
      newParams.set("step", "details");
      navigate({ search: newParams.toString() });
    }
  }, [isMobile, showGuidedWizard, id, params, navigate]);

  return (
    <>
      <Dialog
        open={!isMobile}
        fullWidth
        maxWidth={false}
        onClose={onClose}
        sx={{ "& .MuiDialog-paper": { maxWidth: "1050px" } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box width={40} />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography color="textPrimary" fontSize={24} fontWeight={500}>
                {isVa ? "Task Internal Work Order Picking" : "Internal Work Order Picking"}
              </Typography>
              <ItemCountBadge count={inventoryItems.length || 0} />
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 200 }}>
          <Box>
            {!loading && (
              <InternalWorkOrderHeader
                internalWorkOrder={internalWorkOrder}
                changeStatus={changeStatus}
              />
            )}
            <Box mb={3} display="flex" justifyContent="flex-end" alignItems="flex-start">
              <Box maxWidth={400}>
                <PalletsBuiltField
                  projectId={internalWorkOrder.projectId}
                  value={internalWorkOrder.billingQuantities}
                  open={openPalletsBuiltCountModal}
                  setOpen={setOpenPalletsBuiltCountModal}
                  onSubmit={handlePalletsBuiltCountSubmit}
                />
              </Box>
            </Box>

            {loading ? (
              <Box width="100%" display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : sortedInventoryItems.length === 0 ? (
              <Box pt={7} display="flex" justifyContent="center">
                No items to pick
              </Box>
            ) : (
              <>
                <Box pb={2} display="flex" justifyContent="space-between" alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 320 }}
                  />
                  {hasAssistantPermission && (
                    <Button variant="contained" color="primary" onClick={handleStartPicking}>
                      Internal Order Assistant
                    </Button>
                  )}
                </Box>
                {filteredInventoryItems.length === 0 && searchTerm ? (
                  <Box pt={4} pb={4} display="flex" justifyContent="center">
                    <Typography variant="body2" color="textSecondary">
                      No items found matching "{searchTerm}"
                    </Typography>
                  </Box>
                ) : hasMultipleWarehouses ? (
                  // Render separate tables for each warehouse
                  Object.entries(itemsGroupedByWarehouse).map(
                    ([warehouseName, warehouseItems], index) => (
                      <Box key={warehouseName}>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 0.5,
                            p: 1.5,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            fontWeight: 500,
                            color: "#1976d2",
                          }}
                        >
                          {warehouseName === "Unknown"
                            ? "Items without warehouse assignment"
                            : `Picking from: ${warehouseName}`}
                        </Typography>
                        <InventoryTable
                          filteredInventoryItems={warehouseItems}
                          showBarcodeColumn={showBarcodeColumn}
                          inventoryRequest={internalWorkOrder}
                          advancedPickingMode={true}
                          selectedRowId={selectedRowId}
                          setSelectedRowId={setSelectedRowId}
                          handlePickKit={handlePickKit}
                          project={project}
                          warehouseLookup={warehouseLookup}
                          hasMultipleWarehouses={hasMultipleWarehouses}
                          orderType="internalWorkOrder"
                          showReturnToLocation={allItemsPicked}
                        />
                      </Box>
                    )
                  )
                ) : (
                  // Single table for single warehouse
                  <InventoryTable
                    filteredInventoryItems={filteredInventoryItems}
                    showBarcodeColumn={showBarcodeColumn}
                    inventoryRequest={internalWorkOrder}
                    advancedPickingMode={true}
                    selectedRowId={selectedRowId}
                    setSelectedRowId={setSelectedRowId}
                    handlePickKit={handlePickKit}
                    project={project}
                    warehouseLookup={warehouseLookup}
                    hasMultipleWarehouses={hasMultipleWarehouses}
                    orderType="internalWorkOrder"
                    showReturnToLocation={allItemsPicked}
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {openKitModal && (
        <ManualLocationKit
          inventoryRequestId={id}
          assetId={selectedKitAssetId}
          rackLocations={kitRackLocations}
          item={selectedItem}
          onClose={() => setOpenKitModal(false)}
          orderType="internalWorkOrder"
        />
      )}

      {showGuidedPicking && (
        <GuidedPickingModal
          billingType={billingType}
          inventoryRequestId={id}
          inventoryRequest={internalWorkOrder}
          inventoryItems={filteredInventoryItems}
          onClose={handleGuidedPickingClose}
          onComplete={() => {}}
          orderType="internalWorkOrder"
        />
      )}

      {showGuidedWizard && (
        <InternalWorkOrderGuidedWizardModal
          internalWorkOrder={internalWorkOrder}
          onToggle={handleGuidedWizardClose}
        />
      )}
    </>
  );
}
